import { Trans, useTranslation } from 'react-i18next';
import { BORDER_RADIUSES, COLORS, FONT_WEIGHTS, SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import IconFile from 'images/icons/file.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedSvg from 'verify/components/ThemedSvg';
import ThemedTitle from 'verify/components/ThemedTitle';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const ConfirmFileContainer = styled('div')`
  background-color: ${COLORS.gray4};
  border-radius: ${BORDER_RADIUSES.rounded};
  margin-bottom: ${SPACINGS.base * 8}px;
  min-height: 80px;
  padding: 0px ${SPACINGS.base * 2}px;

  display: flex;
  flex-direction: row;
  justify-content: start;
  align-items: center;
`;

const IconContainer = styled.div`
  margin-right: ${SPACINGS.base}px;
  display: flex;
`;

const FileNameContainer = styled('div')`
  color: ${COLORS.black};
  font-weight: ${FONT_WEIGHTS.semiBold};
  word-break: break-all;
`;

const ButtonContinue = styled(Button)`
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const Minify = styled('div')`
  display: inline;

  img {
    height: 12px;
    margin-bottom: 4px;
  }
`;

interface ConfirmFileUploadViewProps {
  fileName: string;
  isProcessing: boolean;
  handleContinue: () => void;
  handleRetry: () => void;
}

export default function ConfirmFileUploadView(props: ConfirmFileUploadViewProps) {
  const { fileName, isProcessing } = props;

  const [t] = useTranslation();

  return (
    <div>
      <Header>
        <ThemedTitle>{t('confirm-file-upload.title')}</ThemedTitle>
        <ThemedParagraph>{t('confirm-file-upload.prompt')}</ThemedParagraph>
      </Header>

      <ConfirmFileContainer>
        <IconContainer>
          <ThemedSvg>
            <IconFile />
          </ThemedSvg>
        </IconContainer>
        <FileNameContainer>{fileName}</FileNameContainer>
      </ConfirmFileContainer>

      <ButtonContinue
        buttonType={ButtonType.Primary}
        disabled={isProcessing}
        onClick={props.handleContinue}
        data-test='confirm-file-upload__continue'
      >
        {isProcessing ? (
          <span>
            {t('confirm-file-upload.uploading')}
            <Minify>
              <InlineLoadingIndicator />
            </Minify>
          </span>
        ) : (
          t('confirm-file-upload.btn-submit')
        )}
      </ButtonContinue>

      <ThemedParagraph>
        <Trans i18nKey='confirm-file-upload.or-retry'>
          Or
          <a id='confirm-file-upload__upload-link' onClick={props.handleRetry}>
            upload a new file
          </a>
        </Trans>
      </ThemedParagraph>
    </div>
  );
}
